import React from 'react';
import importedMessages from '@lang/messages';
import {notify} from "@contexts/ContextNotificationProvider";
import HelperAjax from "@helpers/HelperAjax";
import {ContextDataStore} from "@helpers/HelperGlobalDataStore";
import {AjaxParam, eventsPubSub} from '@hooks/useSHCore';

/**
 * AjaxParam and eventsPubSub live in @hooks/useSHCore (shared between the new and the
 * old code). They are re-exported here for backward compatibility with the old 'src'
 * project, which imports them from '@components/base/BaseComponent/BaseComponent'.
 */
export {AjaxParam, eventsPubSub};

/**
 * Legacy base class. Used ONLY by the old 'src' project, which still extends
 * 'BaseComponent' (default import). The new 'src25' project uses the useSHCore hook
 * (@hooks/useSHCore).
 *
 * IMPORTANT: The class and the hook share the same eventsPubSub array (see the bottom),
 * so that the Pub/Sub mechanism keeps working between the two codebases.
 */
class BaseComponent extends React.Component {

	componentName = '';
	componentToken = '';
	componentVisible = true;
	loading = false;
	mounted = false;

	constructor(props, context, name) {

		super(props, context);

		let data = null;

		if (name && context) {

			context.bindObj(name, this);

			data = context.get(name);
		}

		if (!data) {
			data = props.data;
		}

		this.state = {};

		if (data) {
			if (!name) {
				name = data.componentName;
			}
			this.componentName = name;
			this.componentToken = data.componentToken;
			this.state = data;
		}

		this.loading = props.isLazy;

		this.messages = importedMessages[SHVO.pageLang];

		if (typeof this.eventConsume === "function") {
			this.eventConsume = this.eventConsume.bind(this);
			BaseComponent.eventsPubSub.push(this.eventConsume);
		}

		if (!SHVO.pageComponent) {
			SHVO.pageComponent = this;
		}
	}

	setState(state, callback) {

		if (this.componentName) {
			this.context.set(this.componentName, state);
		}
		super.setState(state, callback);
	}

	getComponentState = () => {
		return this.state;
	};

	componentValidate = () => {
		return true;
	};

	reload = (actionObject, callback = null, showLoading = true) => {

		const ajaxParam = new AjaxParam();
		ajaxParam.callbackSuccess = (data) => {
			this.setState(data);

			if (typeof callback === "function") {
				callback(data);
			}
		};
		ajaxParam.isLoading = showLoading;
		ajaxParam.cypressParamString = '?actionReload' + this.componentName;
		this.ajaxCall('ActionGetDefaultState', actionObject, ajaxParam);
	};

	eventRaise = (eventName, eventData) => {
		BaseComponent.eventsPubSub.forEach(function (func) {
			func(eventName, eventData);
		});
	};

	componentDidMount() {

		if (!this.props.data) {
			return;
		}
		const {
			componentToken,
			componentName
		} = this.props.data;

		if (componentToken) {
			this.componentToken = componentToken;
			this.componentName = componentName;
		}

		this.mounted = true;

		const {isLazy} = this.props;

		if (isLazy == '1') {
			let ajaxParam = new AjaxParam();
			ajaxParam.callbackSuccess = (data) => {
				if (SHVO.pageComponent.componentName === this.componentName) {
					SHVO.pageState = data;
				}

				if (data.componentToken) {
					this.componentToken = data.componentToken;
				}
				this.setState(data);

				this.eventRaise('LazyLoadingComplete', data);
			};
			ajaxParam.cypressParamString = '?LazyLoading-' + componentName;
			this.ajaxCall('ActionGetDefaultState', {}, ajaxParam);
		}
	}

	componentWillUnmount() {
		this.mounted = false;
		if (this.componentName) {
			this.context.bindObj(this.componentName, null);
		}
	}

	static getDerivedStateFromProps(nextProps, prevState) {

		if (nextProps.data) {

			const {
				componentNewURL,
				componentInital,
			} = nextProps.data;

			if (componentInital) {
				return null;
			}
			if ((componentNewURL !== '') && (componentNewURL !== undefined)) {
				window.location = componentNewURL;
				return null;
			}
		}

		return prevState;
	}

	getLink = (pageURL, actionId = '', useTabId = false) => {
		if (actionId !== '') {
			actionId = '/a' + actionId;
		}
		let tabId = '';

		if (useTabId) {
			tabId = this.getPageTabId();

			if (tabId !== '') {
				tabId = '/t' + tabId;
			}
		}

		return '/' + pageURL + actionId + tabId;
	};

	// noinspection JSMethodCanBeStatic
	getPageTabId = () => {
		if (typeof (sessionStorage) === 'object') {
			let key = 'BaseComponentTabId';
			let tabId = sessionStorage.getItem(key);
			if (tabId === null) {
				tabId = Math.random().toString(36).substr(2, 10);
				sessionStorage.setItem(key, tabId);

			}
			return tabId;
		}
	};

	/**
	 * @return BaseComponent
	 */
	rootPage = () => {
		return SHVO.pageComponent;
	}

	ajaxCall = (actionName, actionData, ajaxParam = null) => {

		if (ajaxParam !== null) {
			let isValid = (typeof ajaxParam == "object") && ajaxParam.hasOwnProperty('isLoading');
			if (!isValid) {
				throw "ajaxParam must be an instance of 'AjaxParam'";
			}
		} else {
			ajaxParam = new AjaxParam();
		}

		let comName = this.componentName;
		let comCSFR = this.componentToken;
		let comPTId = this.getPageTabId();

		let callbackSuccess = ajaxParam.callbackSuccess;
		let callbackError = ajaxParam.callbackError;

		let nowTS = (parseInt(Date.now() / 1000) + SHVO.pageTsDelta).toString(23);
		nowTS = Math.random().toString(8).substr(2, 10) + nowTS.split('').reverse().join('');

		this.loading = ajaxParam.isLoading;

		if (this.loading) {
			this.forceUpdate();
		}

		let body = {
			action_ptid: comPTId,
			action_name: actionName,
			action_data: actionData,
			component_name: comName,
			component_token_csfr: comCSFR,
			component_token_dtsc: nowTS,
			component_url: SHVO.pageURL,
			page_project: SHVO.pageProject,
		};

		let url = SHVO.pageAGW;
		if (window.cypressTest === true) {
			url = url + ajaxParam.cypressParamString;
		}

		HelperAjax.fetch('POST', url, JSON.stringify(body), {
			'x-token-dtsc': nowTS,
			'x-token-csfr': comCSFR,
			'x-http-referer': window.location.href,
			'Content-Type': 'application/json; charset=utf-8'
		}).then(response => {
			let code = response.respCode;
			let data = response.respBody;

			if (code == '205') {
				location.reload(true);
				return;
			}

			if (!data) {
				return;
			}
			try {
				data = JSON.parse(data);

				const {
					componentNewURL,
					componentErrors
				} = data;

				if (componentNewURL) {
					this.navigate(componentNewURL);
					return;
				}
				this.loading = false;

				if (data.componentFlat) {
					this.context.init(data.componentFlat);
				}

				let hasErrors = ((typeof componentErrors === 'object') && (componentErrors.length > 0));

				if (hasErrors) {
					if (typeof callbackError === "function") {
						callbackError(data);
						return;
					} else {
						const errorMessage = componentErrors[0];
						notify.error(this.messages[errorMessage] || errorMessage);
					}
				} else {
					if (typeof callbackSuccess === "function") {
						callbackSuccess(data);
						return;
					}
				}

				this.setState(data);

			} catch (error) {
				console.error(error);
			}

		}).catch(function (error) {
			console.error(error);
		});
	};

	navigate = (url) => {
		if ((url !== '') && (url !== undefined)) {
			window.location = url;
		}
	};
}

BaseComponent.contextType = ContextDataStore;
BaseComponent.eventsPubSub = eventsPubSub;

export default BaseComponent;
